import { apiClient } from './client';
import type {
  HomeDashboardDto,
  HomeDashboardViewModel,
  HomeKnowledgeDto,
  HomeTaskDto,
} from '../types/home';
import { RUNTIME_API_CONFIG } from '../config/runtimeApi';

const HOME_DASHBOARD_PATH = '/api/v1/student/home';

const fallbackHomeDashboardDto: HomeDashboardDto = {
  user: {
    id: 'demo-user',
    display_name: '学习达人',
    avatar_label: '学',
    grade_name: '六年级',
  },
  summary: {
    total_tasks: 4,
    completed_tasks: 1,
    streak_days: 7,
    mastered_wrong_count: 12,
  },
  today_tasks: [
    {
      id: 'task-photo-search',
      title: '完成数学周测错题复盘',
      subject: '数学',
      scheduled_at: '14:00',
      reward_label: '+3',
      completed: false,
    },
    {
      id: 'task-words',
      title: '英语单词打卡',
      subject: '英语',
      scheduled_at: '20:00',
      reward_label: '+2',
      completed: false,
    },
  ],
  knowledge_cards: [
    {
      id: 'knowledge-function',
      title: '二次函数最值',
      description: '配方法和图像顶点仍需要复习',
      mastery_percent: 46,
    },
    {
      id: 'knowledge-force',
      title: '浮力计算',
      description: '公式识别稳定，单位换算要加强',
      mastery_percent: 68,
    },
  ],
};

const clampPercent = (value: number | null): number => {
  if (value === null || Number.isNaN(value)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.round(value)));
};

const mapTask = (task: HomeTaskDto): HomeDashboardViewModel['todayPlans'][number] => ({
  id: task.id,
  title: task.title ?? '未命名任务',
  subject: task.subject ?? '综合',
  scheduledAt: task.scheduled_at ?? '未安排',
  rewardLabel: task.reward_label ?? '',
  completed: task.completed ?? false,
});

const mapKnowledgeCard = (
  item: HomeKnowledgeDto
): HomeDashboardViewModel['knowledgeCards'][number] => ({
  id: item.id,
  title: item.title ?? '未命名知识点',
  description: item.description ?? '暂无说明',
  masteryPercent: clampPercent(item.mastery_percent),
});

export const mapHomeDashboardDtoToViewModel = (
  dto: HomeDashboardDto
): HomeDashboardViewModel => {
  const todayPlans = (dto.today_tasks ?? []).map(mapTask);
  const knowledgeCards = (dto.knowledge_cards ?? []).map(mapKnowledgeCard);
  const completedTaskCount =
    dto.summary?.completed_tasks ?? todayPlans.filter((task) => task.completed).length;
  const totalTaskCount = dto.summary?.total_tasks ?? todayPlans.length;

  return {
    user: {
      displayName: dto.user?.display_name ?? '同学',
      subtitle: dto.user?.grade_name ?? '今日学习计划',
      avatarLabel: dto.user?.avatar_label ?? '学',
      completedTaskCount,
      totalTaskCount,
    },
    todayPlans,
    knowledgeCards,
    masteredWrongCount: dto.summary?.mastered_wrong_count ?? 0,
    streakDays: dto.summary?.streak_days ?? 0,
  };
};

export const getHomeDashboard = async (): Promise<HomeDashboardViewModel> => {
  if (!RUNTIME_API_CONFIG.enabled) {
    return mapHomeDashboardDtoToViewModel(fallbackHomeDashboardDto);
  }

  const response = await apiClient.get<HomeDashboardDto>(HOME_DASHBOARD_PATH);
  return mapHomeDashboardDtoToViewModel(response.data);
};
